import React, { useMemo, useState } from 'react';
import { Salesman } from '../types';
import { usePos } from '../context/PosContext';
import './Products.css';

// Salesman / Employee Master (employee_detail_fragment.xml): two-pane list +
// form. Salesmen can be attributed on bills (Bill screen) and reported on.
const blank = {
  name: '',
  phone: '',
  shortName: '',
  searchCode: '',
  dateOfJoining: '',
  balance: '',
  extra: '',
  allowAdvance: false,
};

const Salesmen: React.FC = () => {
  const { salesmen, sales, addSalesman, updateSalesman, deleteSalesman, formatMoney } = usePos();
  const [editing, setEditing] = useState<Salesman | null>(null);
  const [listSearch, setListSearch] = useState('');
  const [f, setF] = useState({ ...blank });

  const set = (k: keyof typeof blank, v: string | boolean) => setF((prev) => ({ ...prev, [k]: v }));

  const filtered = useMemo(() => {
    const t = listSearch.trim().toLowerCase();
    if (!t) return salesmen;
    return salesmen.filter(
      (s) =>
        s.name.toLowerCase().includes(t) ||
        s.shortName?.toLowerCase().includes(t) ||
        s.searchCode?.toLowerCase().includes(t) ||
        s.phone?.includes(listSearch)
    );
  }, [salesmen, listSearch]);

  // Per-salesman sales summary (matched by salesman name on completed bills).
  const summary = useMemo(() => {
    if (!editing) return null;
    const theirs = sales.filter(
      (s) => s.status === 'completed' && s.total > 0 && s.salesman === editing.name
    );
    const total = theirs.reduce((a, s) => a + s.total, 0);
    return { bills: theirs.length, total, avg: theirs.length ? total / theirs.length : 0 };
  }, [editing, sales]);

  const reset = () => {
    setF({ ...blank });
    setEditing(null);
  };

  const buildData = (): Omit<Salesman, 'id'> => ({
    name: f.name,
    phone: f.phone || undefined,
    shortName: f.shortName || undefined,
    searchCode: f.searchCode || undefined,
    dateOfJoining: f.dateOfJoining || undefined,
    balance: f.balance ? parseFloat(f.balance) : undefined,
    extra: f.extra || undefined,
    allowAdvance: f.allowAdvance,
  });

  const save = () => {
    if (!f.name || !f.phone) {
      alert('Name and Mobile are required.');
      return;
    }
    if (editing) updateSalesman({ ...editing, ...buildData() });
    else addSalesman(buildData());
    reset();
  };

  const edit = (s: Salesman) => {
    setEditing(s);
    setF({
      name: s.name,
      phone: s.phone ?? '',
      shortName: s.shortName ?? '',
      searchCode: s.searchCode ?? '',
      dateOfJoining: s.dateOfJoining ?? '',
      balance: s.balance != null ? String(s.balance) : '',
      extra: s.extra ?? '',
      allowAdvance: s.allowAdvance ?? false,
    });
  };

  const remove = () => {
    if (editing && window.confirm('Delete this salesman?')) {
      deleteSalesman(editing.id);
      reset();
    }
  };

  const lbl = (t: string) => <label className="pf-lbl">{t}</label>;

  return (
    <div className="pm-page">
      {/* list */}
      <div className="pm-list card-rounded">
        <input
          className="pm-list-search"
          placeholder="Search by Name, Short Name or Search Code"
          value={listSearch}
          onChange={(e) => setListSearch(e.target.value)}
        />
        <div className="pm-list-scroll">
          {filtered.map((s) => (
            <button key={s.id} className={`pm-list-item ${editing?.id === s.id ? 'sel' : ''}`} onClick={() => edit(s)}>
              <span className="pm-li-name">{s.name}</span>
              <span className="pm-li-meta">
                {s.phone || '—'}
                {s.shortName ? ` · ${s.shortName}` : ''}
              </span>
            </button>
          ))}
          {filtered.length === 0 && (
            <p className="muted" style={{ padding: 16 }}>
              No salesmen.
            </p>
          )}
        </div>
      </div>

      {/* form */}
      <div className="pm-form card-rounded">
        <div className="pf-top">
          <span style={{ fontWeight: 700, color: 'var(--color-primary-dark)' }}>
            {editing ? 'Edit Salesman' : 'New Salesman'}
          </span>
          <div className="pf-top-btns">
            {editing && <button className="pf-btn-orange" onClick={reset}>New</button>}
            {editing && <button className="pf-btn-orange" onClick={remove}>Delete</button>}
            <button className="pf-btn-orange" onClick={save}>{editing ? 'Update' : 'Save'}</button>
          </div>
        </div>

        <div className="pf-scroll">
          <div className="pf-row r3">
            <div>{lbl('Name *')}<input value={f.name} onChange={(e) => set('name', e.target.value)} /></div>
            <div>{lbl('Search Code')}<input value={f.searchCode} onChange={(e) => set('searchCode', e.target.value)} /></div>
            <div>{lbl('Short Name')}<input value={f.shortName} onChange={(e) => set('shortName', e.target.value)} /></div>
          </div>
          <div className="pf-row r3">
            <div>{lbl('Mobile *')}<input value={f.phone} onChange={(e) => set('phone', e.target.value)} /></div>
            <div>{lbl('Date of Joining')}<input type="date" value={f.dateOfJoining} onChange={(e) => set('dateOfJoining', e.target.value)} /></div>
            <div>{lbl('Balance')}<input type="number" value={f.balance} onChange={(e) => set('balance', e.target.value)} /></div>
          </div>
          <div className="pf-row r1">
            <div>{lbl('Extra')}<input value={f.extra} onChange={(e) => set('extra', e.target.value)} /></div>
          </div>
          <div className="pf-checks">
            <label>
              <input type="checkbox" checked={f.allowAdvance} onChange={(e) => set('allowAdvance', e.target.checked)} /> Allow advance payment
            </label>
          </div>

          {editing && summary && (
            <>
              <div className="pf-section">Sales Summary</div>
              <div className="pf-row r3">
                <div>{lbl('Bills')}<input value={summary.bills} readOnly /></div>
                <div>{lbl('Total Sales')}<input value={formatMoney(summary.total)} readOnly /></div>
                <div>{lbl('Average Bill')}<input value={formatMoney(summary.avg)} readOnly /></div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Salesmen;
